import type {
  ColumnState,
  FilterModel,
  GridApi,
  IFilter,
  IServerSideGetRowsRequest,
  SortModelItem,
} from 'ag-grid-community';

/**
 * Grid query in the wire shape the backend `GridQueryParser` reads:
 * `page`/`pageSize` (1-based), repeatable `sort=colId:asc|desc`, and one `filter` JSON object.
 */
export interface ServerSideGridArgs {
  page: number;
  pageSize: number;
  sort: string[];
  filter?: string;
}

export type GridQueryBody<TScope extends object> = TScope & {
  page: number;
  pageSize: number;
  sortModel: { colId: string; sort: 'asc' | 'desc' }[];
  filterModel: Record<string, unknown>;
  rowGroupCols: [];
  valueCols: [];
  quickFilterText: '';
};

/** Full key list per column for Set Filters whose values are loaded, used to send the shorter list. */
export type SetFilterUniverses = ReadonlyMap<string, readonly (string | null)[]>;

export interface GridRequestOptions {
  /** Renames grid column ids (e.g. the tree auto group column) to the backend column id. */
  columnAliases?: Readonly<Record<string, string>>;
  universes?: SetFilterUniverses;
}

interface SetModel {
  filterType: 'set';
  values: (string | null)[];
  excluded?: boolean;
}

interface MultiModel {
  filterType: 'multi';
  filterModels: (unknown | null)[] | null;
}

interface SetFilterInstance {
  getFilterKeys: () => (string | null)[];
}

interface MultiFilterInstance {
  getChildFilterInstance: (index: number) => IFilter | undefined;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSetModel = (value: unknown): value is SetModel =>
  isRecord(value) && value.filterType === 'set' && Array.isArray(value.values);

const isMultiModel = (value: unknown): value is MultiModel =>
  isRecord(value) && value.filterType === 'multi';

const isSetFilterInstance = (value: unknown): value is SetFilterInstance =>
  isRecord(value) && typeof value.getFilterKeys === 'function';

const isMultiFilterInstance = (value: unknown): value is MultiFilterInstance =>
  isRecord(value) && typeof value.getChildFilterInstance === 'function';

/** Advanced Filter models carry `filterType: 'join'` or a `colId`; column filter models are keyed by column id. */
const isAdvancedFilterModel = (model: Record<string, unknown>) =>
  model.filterType === 'join' || typeof model.colId === 'string';

const resolveColumnId = (columnId: string, aliases: GridRequestOptions['columnAliases']) =>
  aliases?.[columnId] ?? columnId;

/** Backend `GridQueryParser.MaxSorts`; lower-priority sorts beyond it are dropped client-side. */
export const MAX_SERVER_SIDE_SORTS = 5;

export const serializeSortModel = (
  sortModel: readonly SortModelItem[],
  columnAliases?: GridRequestOptions['columnAliases'],
): string[] =>
  sortModel
    .slice(0, MAX_SERVER_SIDE_SORTS)
    .map(({ colId, sort }) => `${resolveColumnId(colId, columnAliases)}:${sort}`);

/**
 * Sends `excluded: true` with the unticked keys when that list is shorter than the ticked one.
 * Without a loaded universe the ticked keys are sent unchanged. `values: []` stays "match nothing".
 */
export const compactSetFilterModel = (
  model: SetModel,
  universe: readonly (string | null)[] | undefined,
): SetModel => {
  if (model.excluded || !universe?.length) {
    return {
      filterType: 'set',
      values: model.values,
      ...(model.excluded ? { excluded: true } : {}),
    };
  }

  const selected = new Set(model.values);
  const unticked = universe.filter((value) => !selected.has(value));

  return unticked.length < model.values.length
    ? { filterType: 'set', values: unticked, excluded: true }
    : { filterType: 'set', values: model.values };
};

const compactColumnModel = (model: unknown, universe: readonly (string | null)[] | undefined) => {
  if (isSetModel(model)) {
    return compactSetFilterModel(model, universe);
  }

  if (isMultiModel(model)) {
    return {
      filterType: 'multi',
      filterModels: (model.filterModels ?? []).map((child) =>
        isSetModel(child) ? compactSetFilterModel(child, universe) : (child ?? null),
      ),
    };
  }

  return model;
};

export const serializeFilterModel = (
  filterModel: IServerSideGetRowsRequest['filterModel'],
  { columnAliases, universes }: GridRequestOptions = {},
): string | undefined => {
  if (!filterModel) {
    return undefined;
  }

  const model = filterModel as Record<string, unknown>;

  if (isAdvancedFilterModel(model)) {
    throw new Error('Advanced Filter is not supported on server-side grids.');
  }

  const entries = Object.entries(model).filter(([, columnModel]) => columnModel != null);

  if (!entries.length) {
    return undefined;
  }

  return JSON.stringify(
    Object.fromEntries(
      entries.map(([columnId, columnModel]) => [
        resolveColumnId(columnId, columnAliases),
        compactColumnModel(columnModel, universes?.get(columnId)),
      ]),
    ),
  );
};

/** Converts existing datasource args into the JSON body shared by the four SSRM query endpoints. */
export const toGridQueryBody = <TScope extends object>(
  args: ServerSideGridArgs,
  scope: TScope,
): GridQueryBody<TScope> => ({
  ...scope,
  page: args.page,
  pageSize: args.pageSize,
  sortModel: args.sort.map((entry) => {
    const separator = entry.lastIndexOf(':');
    const colId = entry.slice(0, separator);
    const sort = entry.slice(separator + 1);

    if (separator <= 0 || !colId || (sort !== 'asc' && sort !== 'desc')) {
      throw new Error(`Invalid server-side sort entry: ${entry}`);
    }

    return { colId, sort };
  }),
  filterModel: args.filter ? (JSON.parse(args.filter) as Record<string, unknown>) : {},
  rowGroupCols: [],
  valueCols: [],
  quickFilterText: '',
});

/**
 * Maps one SSRM block request to the wire args. The page comes from the block bounds, so it stays
 * correct even while `cacheBlockSize` and `paginationPageSize` briefly differ.
 */
export const toServerSideGridArgs = (
  request: Pick<IServerSideGetRowsRequest, 'startRow' | 'endRow' | 'sortModel' | 'filterModel'>,
  options: GridRequestOptions = {},
): ServerSideGridArgs => {
  const { startRow, endRow } = request;

  if (startRow == null || endRow == null || endRow <= startRow) {
    throw new Error('Server-side grids need a paged block request (startRow/endRow).');
  }

  const pageSize = endRow - startRow;
  const filter = serializeFilterModel(request.filterModel, options);

  return {
    page: Math.floor(startRow / pageSize) + 1,
    pageSize,
    sort: serializeSortModel(request.sortModel, options.columnAliases),
    ...(filter ? { filter } : {}),
  };
};

/** Appends grid args with `URLSearchParams.append`, because RTK `params` would comma-join `sort`. */
export const appendGridQueryParams = (params: URLSearchParams, args: ServerSideGridArgs) => {
  params.set('page', String(args.page));
  params.set('pageSize', String(args.pageSize));
  args.sort.forEach((sort) => params.append('sort', sort));

  if (args.filter) {
    params.set('filter', args.filter);
  }

  return params;
};

/** Current sort in priority order, read from column state (for requests the grid didn't issue). */
export const getGridSortModel = <T>(api: GridApi<T>): SortModelItem[] =>
  api
    .getColumnState()
    .filter((column): column is ColumnState & { sort: 'asc' | 'desc' } => !!column.sort)
    .sort((left, right) => (left.sortIndex ?? 0) - (right.sortIndex ?? 0))
    .map(({ colId, sort }) => ({ colId, sort }));

/** Reads the loaded key list of every Set Filter (top-level or inside a multi filter) in the model. */
export const resolveSetFilterUniverses = async <T>(
  api: GridApi<T>,
  filterModel: FilterModel | null | undefined,
): Promise<SetFilterUniverses> => {
  const universes = new Map<string, (string | null)[]>();

  if (!filterModel) {
    return universes;
  }

  await Promise.all(
    Object.entries(filterModel).map(async ([columnId, columnModel]) => {
      const setChildIndex = isMultiModel(columnModel)
        ? (columnModel.filterModels ?? []).findIndex(isSetModel)
        : isSetModel(columnModel)
          ? -1
          : null;

      if (setChildIndex === null || (isMultiModel(columnModel) && setChildIndex < 0)) {
        return;
      }

      const instance: unknown = await api.getColumnFilterInstance(columnId);
      const setInstance =
        setChildIndex >= 0 && isMultiFilterInstance(instance)
          ? instance.getChildFilterInstance(setChildIndex)
          : instance;

      if (!isSetFilterInstance(setInstance)) {
        return;
      }

      const keys = setInstance.getFilterKeys();

      if (keys.length) {
        universes.set(columnId, keys);
      }
    }),
  );

  return universes;
};
